import { Link } from 'react-router-dom';
import { Zap, Skull, Flame, TrendingUp, Target, Shield } from 'lucide-react';

export default function BlogPolymarketDegenCopyTrading() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        {/* Header */}
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-600/15 border border-pink-500/40 text-xs text-pink-100 uppercase tracking-wide">
            <Skull className="w-3 h-3" />
            <span>Polymarket Degen Copy Trading</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Degens, Stop Doomscrolling Screenshots – Copy Real Polymarket Edge With BetMimic
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            If you&apos;re already living on Polymarket, hunting election lines, sports misprices and crypto
            event markets, BetMimic is the degen-friendly copy trading bot that actually plugs into the
            official Polymarket CLOB. No fantasy balances, no screenshot-chasing – just real USDC.e PnL in
            your own wallet.
          </p>
        </header>

        {/* Section: Why every Polymarket degen needs a real copy stack */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Flame className="w-5 h-5 text-primary-400" />
            Why every Polymarket degen needs a real copy stack
          </h2>
          <p className="text-sm text-dark-300">
            The sharpest Polymarket traders don&apos;t post every fill. They live in the order book, pressing
            size into liquidity holes while CT argues about vibes. If you&apos;re trying to copy them manually
            from screenshots, you&apos;re late, off-price and usually on the wrong side of the spread.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Polymarket degen copy trading</span> only works if you copy
              the <em>actual trades</em>, not the flex posts.
            </li>
            <li>
              A real <span className="font-medium">Polymarket copy trading bot</span> has to talk to the
              <span className="font-medium"> official CLOB API</span> and reconcile positions against the
              Data API – exactly what BetMimic does.
            </li>
            <li>
              You want your <span className="font-medium">Polymarket PnL</span> in one place, in your own
              wallet, not scattered across random "bots" and spreadsheets.
            </li>
          </ul>
        </section>

        {/* Section: Degen mode with guardrails */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            Degen mode, but with actual guardrails
          </h2>
          <p className="text-sm text-dark-300">
            BetMimic was built so Polymarket degens can fire size behind elite wallets <em>without</em>
            insta-nuking their roll. You can set up one tame config and one true degen wing, both running
            off the same wallet and USDC.e balance.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Max exposure</span> per config so one trader can&apos;t quietly
              stack your entire bankroll across 20 correlated markets.
            </li>
            <li>
              <span className="font-medium">Daily loss caps</span> – once you hit the pain number in USDC.e,
              BetMimic stops copying new Polymarket trades for that config.
            </li>
            <li>
              <span className="font-medium">Per-position stop loss / take profit</span> with a backend
              monitor that actually closes things on the order book.
            </li>
            <li>
              <span className="font-medium">Liquidity and volume filters</span> so your degen copy bot
              doesn&apos;t slam full size into a 3 USDC meme market.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            Yes, you can still blow up if you disable every rule. But if you run sane caps, BetMimic makes
            it much harder to lose everything on one "I swear this election is free money" idea.
          </p>
        </section>

        {/* Section: Who this is for */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            BetMimic is built for:
          </h2>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <strong>Polymarket degens</strong> who want an always-on copy trading bot that mirrors sharp
              wallets into <span className="font-medium">their own USDC.e wallet</span>.
            </li>
            <li>
              Traders searching for a <strong>Polymarket copy trading tool</strong> or
              <strong>BetMimic Polymarket bot</strong> that respects real risk rules.
            </li>
            <li>
              People googling <strong>Polymarket degen strategies</strong>,
              <strong>copy trade Polymarket whales</strong>, or
              <strong>automated BetMimic Polymarket copy trading</strong> instead of building it from
              scratch.
            </li>
            <li>
              Crypto natives who want <strong>prediction market edge</strong> without living 24/7 in the
              order book.
            </li>
          </ul>
        </section>

        {/* Section: Quick start for degens */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            7-minute degen setup on BetMimic
          </h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-1">
            <li>Create or use an existing Polymarket wallet and fund it with USDC.e on Polygon.</li>
            <li>On BetMimic, go to <span className="font-medium">Settings → Wallet</span> and plug in your details.</li>
            <li>
              Pick one or two traders from the <Link to="/traders" className="text-primary-300">Traders</Link>{' '}
              page with real volume and a track record.
            </li>
            <li>
              Create a copy config: start with small fixed sizing, then add a separate "degen wing" config
              with higher limits once you trust the flow.
            </li>
            <li>
              Turn on liquidity filters and stop loss, so your Polymarket degen copy trading stays inside
              the rails you define.
            </li>
            <li>Enable the config and watch new copied trades hit your Positions tab and Polymarket history.</li>
          </ol>
        </section>

        {/* Section: Internal links / CTA */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            If you&apos;re already a Polymarket degen, BetMimic is the missing piece between "scrolling X" and
            running an actual copy trading book with controls. You get the same edge, more discipline, and
            less FOMO.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/tutorial"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <Target className="w-4 h-4" />
              Read the full BetMimic tutorial
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Zap className="w-4 h-4" />
              See BetMimic plans for Polymarket degens
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
